import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate, RESERVATION_TYPE_LABELS, STATUS_BADGE, todayISO } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Check, X, Info, Pencil, Trash2, BookOpen, History, Fish, CloudRain, Sun, Cloud, CloudSun, CloudSnow, CloudLightning, CloudFog, Wind, Compass, Anchor, Ship } from "lucide-react";
import ReservationDialog from "@/components/ReservationDialog";
import JournalDialog from "@/components/JournalDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  // Leading: days from previous month
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    cells.push({ date: d, inMonth: false });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), inMonth: true });
  }
  // Trailing: pad to multiple of 7 with next-month days
  let tail = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ date: new Date(year, month + 1, tail++), inMonth: false });
  }
  return cells;
}

function isoDay(d) {
  if (!d) return "";
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Owner-specific palette for private reservations
// Rok → cyan, Borut → rose. Protected = deeper saturation.
const OWNER_PALETTE = [
  { privateBg: "bg-[#22D3EE]/75", protectedBg: "bg-[#0891B2]", dot: "bg-[#22D3EE]", textShort: "Rok" },
  { privateBg: "bg-[#F472B6]/75", protectedBg: "bg-[#BE185D]", dot: "bg-[#F472B6]", textShort: "Borut" },
];

function paletteFor(ownerId, owners) {
  if (!ownerId || !Array.isArray(owners)) return null;
  const idx = owners.findIndex(o => o.id === ownerId);
  if (idx < 0) return null;
  return OWNER_PALETTE[idx % OWNER_PALETTE.length];
}

function reservationColor(r, owners = []) {
  if (r.not_used) return "bg-[#475569]/60";
  if (r.status === "rejected" || r.status === "cancelled") return "bg-[#94A3B8]/40";
  if (r.reservation_type === "maintenance_block") return "bg-[#EF4444]";
  if (r.reservation_type === "private") {
    const p = paletteFor(r.owner_id, owners);
    if (p) return r.is_protected_private_term ? p.protectedBg : p.privateBg;
    // fallback if owner not yet loaded / unknown
    return r.is_protected_private_term ? "bg-[#007AFF]" : "bg-[#4DA6FF]/70";
  }
  if (r.status === "pending_approval") return "bg-[#F59E0B]";
  if (r.status === "approved") return "bg-[#10B981]";
  return "bg-[#94A3B8]";
}

// Map a WMO weather code to a small icon + colour. Returns null for unknowns.
function weatherGlyph(wc) {
  if (wc === 0) return { Icon: Sun, cls: "text-[#FBBF24]", key: "clear" };
  if (wc === 1 || wc === 2) return { Icon: CloudSun, cls: "text-[#FBBF24]", key: "partly" };
  if (wc === 3) return { Icon: Cloud, cls: "text-[#94A3B8]", key: "cloudy" };
  if (wc === 45 || wc === 48) return { Icon: CloudFog, cls: "text-[#94A3B8]", key: "fog" };
  if ((wc >= 71 && wc <= 77) || wc === 85 || wc === 86) return { Icon: CloudSnow, cls: "text-[#BAE6FD]", key: "snow" };
  if (wc >= 95) return { Icon: CloudLightning, cls: "text-[#A78BFA]", key: "storm" };
  if ((wc >= 51 && wc <= 67) || (wc >= 80 && wc <= 82)) return { Icon: CloudRain, cls: "text-[#4DA6FF]", key: "rain" };
  return null;
}

// Tiny weather indicator shown in the corner of each calendar day cell.
function DayWeather({ wx, t, iso }) {
  if (!wx || wx.weather_code == null) return null;
  const g = weatherGlyph(wx.weather_code);
  if (!g) return null;
  const windy = (wx.wind_max_kt || 0) >= 18;
  const parts = [t(`calendar.wx.${g.key}`)];
  if (wx.temp_max_c != null) parts.push(`${Math.round(wx.temp_max_c)}°${wx.temp_min_c != null ? "/" + Math.round(wx.temp_min_c) + "°" : ""}`);
  if (wx.wind_max_kt != null) parts.push(`${t("calendar.wx.wind")} ${Math.round(wx.wind_max_kt)}kt`);
  if (wx.precip_mm) parts.push(`${wx.precip_mm}mm`);
  const G = g.Icon;
  return (
    <span className="flex items-center gap-0.5 shrink-0" title={parts.join(" · ")} data-testid={`cal-weather-${iso}`}>
      {windy && <Wind className="w-3 h-3 text-[#38BDF8]" />}
      <G className={`w-3.5 h-3.5 ${g.cls}`} />
    </span>
  );
}


const TRIP_TYPES = ["captains_log", "fishing_log", "excursion_log", "charter_log"];

// Icon + colour for a journal-log type shown on the calendar.
function logGlyph(type) {
  switch (type) {
    case "captains_log": return { Icon: BookOpen, cls: "text-[#4DA6FF]" };
    case "fishing_log": return { Icon: Fish, cls: "text-[#34D399]" };
    case "excursion_log": return { Icon: Compass, cls: "text-[#FBBF24]" };
    case "charter_log": return { Icon: Ship, cls: "text-[#A78BFA]" };
    default: return { Icon: BookOpen, cls: "text-[#94A3B8]" };
  }
}

export default function CalendarPage() {
  const { user } = useAuth();
  const t = useT();
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [assets, setAssets] = useState([]);
  const [owners, setOwners] = useState([]);
  const [users, setUsers] = useState([]);
  const [assetFilter, setAssetFilter] = useState("");
  const [reservations, setReservations] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mobileView, setMobileView] = useState("agenda"); // agenda | grid (mobile-only toggle)
  const [weatherByDate, setWeatherByDate] = useState({});
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(true);
  const [editingLog, setEditingLog] = useState(null);

  const grid = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  // Journal logs grouped by day (respecting the asset filter) for calendar markers.
  const logsByDate = useMemo(() => {
    const m = {};
    for (const lg of logs) {
      if (assetFilter && lg.asset_id !== assetFilter) continue;
      if (!lg.log_date) continue;
      (m[lg.log_date] = m[lg.log_date] || []).push(lg);
    }
    return m;
  }, [logs, assetFilter]);

  // Historical/forecast weather for the visible grid (one icon per day).
  useEffect(() => {
    const wxStart = isoDay(new Date(cursor.getFullYear(), cursor.getMonth(), 1 - 7));
    const wxEnd = isoDay(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 7));
    api.get("/weather/daily", { params: { start: wxStart, end: wxEnd } })
      .then(({ data }) => setWeatherByDate(data || {}))
      .catch(() => setWeatherByDate({}));
  }, [cursor]);

  const reload = async () => {
    const [{ data: a }, { data: u }] = await Promise.all([
      api.get("/assets"),
      api.get("/users"),
    ]);
    setAssets(a);
    setUsers(u);
    // Owner dropdown should include any user that can OWN a reservation.
    // We treat both `owner_director` and `super_admin` users as eligible owners
    // (in production the actual boat owner Rok Kobal is a super_admin), so the
    // dropdown is never empty when a director hasn't been seeded yet.
    setOwners(u.filter(x => x.role === "owner_director" || x.role === "super_admin"));
    const rangeStart = isoDay(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    const rangeEnd = isoDay(new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0));
    const params = { start: rangeStart, end: rangeEnd };
    if (assetFilter) params.asset_id = assetFilter;
    const { data: r } = await api.get("/reservations", { params });
    setReservations(r);
    const { data: lg } = await api.get("/daily-logs");
    setLogs(lg || []);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cursor, assetFilter]);

  const reservationsOn = (day) => {
    if (!day) return [];
    const iso = isoDay(day);
    return reservations.filter(r => iso >= r.start_date && iso <= r.end_date);
  };

  const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const canCreate = ["super_admin", "owner_director", "operations_admin"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="p-2 rounded-md bg-[#0B1221] border border-[#1E293B] hover:bg-[#131C31]"
            data-testid="cal-prev-btn" aria-label={t("calendar.prevMonth")}
          ><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-display text-xl font-bold tracking-tight min-w-[200px] text-center" data-testid="cal-month-label">
            {monthLabel}
          </div>
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="p-2 rounded-md bg-[#0B1221] border border-[#1E293B] hover:bg-[#131C31]"
            data-testid="cal-next-btn" aria-label={t("calendar.nextMonth")}
          ><ChevronRight className="w-4 h-4" /></button>
          <button
            onClick={() => setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
            className="ml-2 px-3 py-2 rounded-md bg-[#0B1221] border border-[#1E293B] hover:bg-[#131C31] text-xs font-mono"
            data-testid="cal-today-btn"
          >{t("calendar.today")}</button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLogs(s => !s)}
            data-testid="cal-toggle-logs"
            title={t("calendar.logsToggleHint")}
            className={`px-3 py-2 rounded-md border text-xs font-mono flex items-center gap-1.5 transition-colors ${showLogs ? "bg-[#0B1221] border-[#334155] text-[#CBD5E1]" : "bg-[#0B1221] border-[#1E293B] text-[#64748B]"}`}
          >
            <BookOpen className="w-3.5 h-3.5" /> {showLogs ? t("calendar.logsOn") : t("calendar.logsOff")}
          </button>
          <select
            value={assetFilter} onChange={(e) => setAssetFilter(e.target.value)}
            className="bg-[#0B1221] border border-[#1E293B] rounded-md px-3 py-2 text-sm"
            data-testid="cal-asset-filter"
          >
            <option value="">{t("calendar.allAssets")}</option>
            {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {canCreate && (
            <Button
              onClick={() => { setSelectedDay(todayISO()); setShowNew(true); }}
              data-testid="cal-new-reservation-btn"
              className="bg-[#007AFF] hover:bg-[#0056B3]"
            >
              <Plus className="w-4 h-4 mr-1" /> {t("calendar.newReservation")}
            </Button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[11px] text-[#94A3B8]" data-testid="cal-legend">
        {owners.map((o, idx) => {
          const p = OWNER_PALETTE[idx % OWNER_PALETTE.length];
          const first = o.name.split(" ")[0];
          return (
            <React.Fragment key={o.id}>
              <LegendDot color={p.protectedBg} label={t("calendar.legend.ownerProtected", { name: first })} />
              <LegendDot color={p.privateBg} label={t("calendar.legend.ownerPrivate", { name: first })} />
            </React.Fragment>
          );
        })}
        <LegendDot color="bg-[#F59E0B]" label={t("calendar.legend.pending")} />
        <LegendDot color="bg-[#10B981]" label={t("calendar.legend.approved")} />
        <LegendDot color="bg-[#EF4444]" label={t("calendar.legend.maintenance")} />
        <div className="flex items-center gap-2" data-testid="cal-legend-void">
          <span className="w-3 h-3 rounded-sm bg-[#475569]" style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(2,6,20,0.6) 0, rgba(2,6,20,0.6) 2px, transparent 2px, transparent 4px)" }} />
          <span>{t("calendar.legend.notUsed")}</span>
        </div>
        {showLogs && (
          <>
            <div className="flex items-center gap-2" data-testid="cal-legend-daytrip">
              <BookOpen className="w-3.5 h-3.5 text-[#4DA6FF]" />
              <span>{t("calendar.legend.dayTrip")}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="cal-legend-ondock">
              <Anchor className="w-3.5 h-3.5 text-[#94A3B8]" />
              <span>{t("calendar.legend.onDock")}</span>
            </div>
          </>
        )}
      </div>

      {/* Mobile view toggle */}
      <div className="md:hidden flex items-center gap-1 bg-[#0B1221] border border-[#1E293B] rounded-md p-1 w-fit" data-testid="cal-mobile-view-toggle">
        {[
          { k: "agenda", label: t("calendar.viewAgenda") },
          { k: "grid", label: t("calendar.viewGrid") },
        ].map(v => (
          <button key={v.k} onClick={() => setMobileView(v.k)}
            data-testid={`cal-mobile-view-${v.k}`}
            className={`px-3 py-1.5 rounded text-[11px] font-mono tracking-widest ${mobileView === v.k ? "bg-[#007AFF]/20 text-[#F8FAFC]" : "text-[#94A3B8]"}`}>
            {v.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className={`ledger-card overflow-x-auto ${mobileView === "grid" ? "block" : "hidden"} md:block`}>
        <div className="grid grid-cols-7 border-b border-[#1E293B] bg-[#070D1B]">
          {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map(d => (
            <div key={d} className="px-2 py-2 text-[10px] font-mono tracking-widest text-[#94A3B8] text-center border-r border-[#1E293B] last:border-r-0">{t(`calendar.days.${d}`)}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((cell, idx) => {
            const day = cell.date;
            const inMonth = cell.inMonth;
            const res = reservationsOn(day);
            const isToday = isoDay(day) === todayISO();
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            return (
              <div
                key={idx}
                onClick={() => inMonth && canCreate && (setSelectedDay(isoDay(day)), setShowNew(true))}
                className={`min-h-[108px] border-b border-r border-[#1E293B] last-in-row:border-r-0 p-2 transition-colors ${inMonth ? "cursor-pointer hover:bg-[#131C31]" : "bg-[#050A14]/60"} ${isWeekend && inMonth ? "bg-[#070D1B]" : ""}`}
                data-testid={`cal-day-${isoDay(day)}`}
              >
                <div className={`text-xs font-mono mb-1 flex items-center justify-between ${isToday ? "text-[#4DA6FF] font-bold" : inMonth ? "text-[#94A3B8]" : "text-[#475569]"}`}>
                  <span>{day.getDate()}</span>
                  <span className="flex items-center gap-1">
                    <DayWeather wx={weatherByDate[isoDay(day)]} t={t} iso={isoDay(day)} />
                    {isToday && <span className="badge-pill badge-protected !py-0 !px-1.5 text-[9px]">{t("calendar.now")}</span>}
                  </span>
                </div>
                <div className="space-y-1">
                  {res.slice(0, 3).map((r) => {
                    const owner = owners.find(o => o.id === r.owner_id);
                    const assetShort = assets.find(a => a.id === r.asset_id)?.name?.split(" ").slice(-1)[0] || "";
                    const typeLabel = t(`reservation.${r.reservation_type}`);
                    const label = r.reservation_type === "private" && owner
                      ? `${owner.name.split(" ")[0]} · ${assetShort}`
                      : `${typeLabel.split(" ")[0]} · ${assetShort}`;
                        const iso = isoDay(day);
                        const isStart = iso === r.start_date;
                        const isEnd = iso === r.end_date;
                        const isSpan = r.start_date !== r.end_date;
                        // Visual continuity for multi-day reservations: label on start day only,
                        // continuation rendered as connected bar on subsequent days.
                        const showLabel = !isSpan || isStart;
                        // Rounded on outer edges only so the bar visually flows across days
                        const radiusClass = !isSpan
                          ? "rounded"
                          : isStart
                            ? "rounded-l rounded-r-none"
                            : isEnd
                              ? "rounded-r rounded-l-none"
                              : "rounded-none";
                        return (
                          <button
                            key={r.id}
                            onClick={(e) => { e.stopPropagation(); setDetail(r); }}
                            data-testid={`cal-res-${r.id}-${iso}`}
                            title={`${owner ? owner.name + " · " : ""}${typeLabel}${isSpan ? ` · ${r.start_date} → ${r.end_date}` : ""}${r.not_used ? ` · ${t("calendar.void")}` : ""}`}
                            className={`w-full text-left text-[10px] ${radiusClass} px-1.5 py-0.5 text-white truncate ${reservationColor(r, owners)} hover:opacity-90`}
                            style={r.not_used ? { backgroundImage: "repeating-linear-gradient(45deg, rgba(2,6,20,0.55) 0, rgba(2,6,20,0.55) 3px, transparent 3px, transparent 6px)" } : undefined}
                          >
                            {showLabel
                              ? (r.not_used
                                  ? <span className="inline-flex items-center gap-1 max-w-full"><span className="font-bold text-[8px] leading-none px-1 py-0.5 rounded bg-black/55 tracking-wider shrink-0">{t("calendar.void")}</span><span className="line-through opacity-85 truncate">{label}</span></span>
                                  : label)
                              : <span className="opacity-0 select-none">·</span>}
                          </button>
                        );
                      })}
                      {res.length > 3 && <div className="text-[10px] text-[#94A3B8]">+{res.length - 3} {t("calendar.more")}</div>}
                      {showLogs && (() => {
                        const dayLogs = logsByDate[isoDay(day)] || [];
                        const trips = dayLogs.filter(l => TRIP_TYPES.includes(l.log_type));
                        const stays = dayLogs.filter(l => l.log_type === "dock_overnight");
                        if (!trips.length && !stays.length) return null;
                        const iso = isoDay(day);
                        return (
                          <>
                            {trips.slice(0, 2).map((l) => {
                              const g = logGlyph(l.log_type);
                              const G = g.Icon;
                              const who = (l.skipper_name || "").split(" ")[0];
                              return (
                                <button
                                  key={l.id}
                                  onClick={(e) => { e.stopPropagation(); setEditingLog(l); }}
                                  data-testid={`cal-log-${l.id}-${iso}`}
                                  title={`${t(`journal.logType.${l.log_type}`)} · ${l.skipper_name || ""}${l.place_name ? " · " + l.place_name : ""}`}
                                  className="w-full text-left text-[10px] rounded px-1.5 py-0.5 truncate bg-[#0B1221] border border-[#1E293B] hover:border-[#334155] flex items-center gap-1"
                                >
                                  <G className={`w-3 h-3 shrink-0 ${g.cls}`} />
                                  <span className="truncate text-[#CBD5E1]">{who || t(`journal.logType.${l.log_type}`)}</span>
                                </button>
                              );
                            })}
                            {trips.length > 2 && <div className="text-[9px] text-[#94A3B8]">+{trips.length - 2} {t("calendar.more")}</div>}
                            {stays.length > 0 && (
                              <div className="flex items-center flex-wrap gap-1">
                                {stays.slice(0, 5).map((l) => (
                                  <button
                                    key={l.id}
                                    onClick={(e) => { e.stopPropagation(); setEditingLog(l); }}
                                    data-testid={`cal-stay-${l.id}-${iso}`}
                                    title={`${t("journal.logType.dock_overnight")} · ${l.skipper_name || ""}`}
                                    className="inline-flex items-center gap-0.5 text-[9px] rounded px-1 py-0.5 bg-[#475569]/20 border border-[#475569]/40 text-[#94A3B8] hover:text-[#CBD5E1]"
                                  >
                                    <Anchor className="w-2.5 h-2.5" />{(l.skipper_name || "?").charAt(0)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile agenda list — guaranteed readable on any phone */}
      <div className={`${mobileView === "agenda" ? "block" : "hidden"} md:hidden space-y-2`} data-testid="cal-mobile-agenda">
        <MobileAgenda
          reservations={reservations}
          viewDate={cursor}
          owners={owners}
          assets={assets}
          logsByDate={logsByDate}
          showLogs={showLogs}
          onOpen={(r) => setDetail(r)}
          onOpenLog={(l) => setEditingLog(l)}
          onNewOnDay={(iso) => { setSelectedDay(iso); setShowNew(true); }}
          canCreate={canCreate}
          t={t}
        />
      </div>

      {showNew && (
        <ReservationDialog
          open={showNew}
          onOpenChange={setShowNew}
          assets={assets}
          defaultDate={selectedDay || todayISO()}
          onSaved={() => { setShowNew(false); reload(); toast.success("Reservation saved"); }}
        />
      )}

      {detail && <ReservationDetailDialog reservation={detail} onClose={() => setDetail(null)} onChanged={reload} assets={assets} owners={owners} users={users} />}

      {editingLog && (
        <JournalDialog
          open={!!editingLog}
          onOpenChange={(o) => !o && setEditingLog(null)}
          assets={assets}
          reservations={reservations}
          editing={editingLog}
          onSaved={() => { setEditingLog(null); reload(); }}
        />
      )}
    </div>
  );
}

function LegendDot({ color, label }) {
  return <div className="flex items-center gap-2"><span className={`w-3 h-3 rounded-sm ${color}`} /> <span>{label}</span></div>;
}

function MobileAgenda({ reservations, viewDate, owners, assets, logsByDate = {}, showLogs = true, onOpen, onOpenLog, onNewOnDay, canCreate, t }) {
  // Group reservations in the visible month by day
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthStart = isoDay(new Date(year, month, 1));
  const monthEnd = isoDay(new Date(year, month + 1, 0));

  const inMonth = reservations.filter(r => {
    // any overlap with the visible month
    return !(r.end_date < monthStart || r.start_date > monthEnd);
  });

  // Build per-day buckets (only days that have at least one reservation, sorted asc)
  const byDate = {};
  inMonth.forEach(r => {
    const start = r.start_date < monthStart ? monthStart : r.start_date;
    const end = r.end_date > monthEnd ? monthEnd : r.end_date;
    // Bucket by start-of-reservation-in-month for a cleaner list
    byDate[start] = byDate[start] || [];
    byDate[start].push({ ...r, _span_start: start, _span_end: end });
  });

  // Day-keyed journal logs within the visible month
  const logDays = {};
  if (showLogs) {
    Object.keys(logsByDate).forEach(d => {
      if (d >= monthStart && d <= monthEnd && logsByDate[d].length) logDays[d] = logsByDate[d];
    });
  }

  const keys = Array.from(new Set([...Object.keys(byDate), ...Object.keys(logDays)])).sort();
  const assetName = (id) => assets.find(a => a.id === id)?.name || "";

  if (keys.length === 0) {
    return (
      <div className="ledger-card p-4 text-xs text-[#94A3B8] text-center" data-testid="cal-agenda-empty">
        {t("calendar.agendaEmpty")}
      </div>
    );
  }

  return (
    <div className="ledger-card divide-y divide-[#1E293B]">
      <div className="px-4 py-2 text-[10px] font-mono tracking-widest text-[#94A3B8] flex items-center justify-between">
        <span>{t("calendar.agendaTitle")} · {keys.length} {t("calendar.agendaDays")}</span>
      </div>
      {keys.map(date => {
        const items = byDate[date] || [];
        const dayLogs = logDays[date] || [];
        const d = new Date(date + "T00:00:00");
        const dayLabel = d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });
        return (
          <div key={date} className="px-4 py-3 space-y-2" data-testid={`cal-agenda-day-${date}`}>
            <div className="flex items-center justify-between">
              <div className="font-mono text-xs text-[#4DA6FF]">{dayLabel}</div>
              {canCreate && (
                <button
                  onClick={() => onNewOnDay(date)} data-testid={`cal-agenda-add-${date}`}
                  className="text-[10px] font-mono tracking-widest text-[#94A3B8] hover:text-[#4DA6FF]"
                >+ {t("calendar.add")}</button>
              )}
            </div>
            <div className="space-y-1.5">
              {items.map(r => {
                const owner = owners.find(o => o.id === r.owner_id);
                const typeLabel = t(`reservation.${r.reservation_type}`);
                const badgeText = r.reservation_type === "private" && owner
                  ? `${owner.name.split(" ")[0]}`
                  : typeLabel.split(" ")[0];
                const spanLabel = r.start_date === r.end_date
                  ? fmtDate(r.start_date)
                  : `${fmtDate(r.start_date)} → ${fmtDate(r.end_date)}`;
                return (
                  <button
                    key={r.id}
                    onClick={() => onOpen(r)}
                    data-testid={`cal-agenda-res-${r.id}`}
                    className="w-full text-left rounded-md border border-[#1E293B] bg-[#050A14] hover:border-[#4DA6FF]/50 p-3 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded text-white font-mono ${reservationColor(r, owners)}`}>
                        {badgeText}
                      </span>
                      <div className="flex items-center gap-1">
                        {r.not_used && <span className="badge-pill bg-[#475569]/60 text-[#E2E8F0] border-[#64748B]/40" data-testid={`cal-agenda-void-${r.id}`}>{t("calendar.void")}</span>}
                        {r.is_protected_private_term && (
                          <span className="badge-pill badge-protected">{t("calendar.protected")}</span>
                        )}
                      </div>
                    </div>
                    <div className={`text-sm text-[#F8FAFC] ${r.not_used ? "line-through opacity-70" : ""}`}>{assetName(r.asset_id)} · {typeLabel}</div>
                    <div className="font-mono text-[10px] text-[#94A3B8] mt-0.5">{spanLabel}</div>
                  </button>
                );
              })}
              {dayLogs.map(l => {
                const isStay = l.log_type === "dock_overnight";
                const g = logGlyph(l.log_type);
                const G = isStay ? Anchor : g.Icon;
                return (
                  <button
                    key={l.id}
                    onClick={() => onOpenLog(l)}
                    data-testid={`cal-agenda-log-${l.id}`}
                    className="w-full text-left rounded-md border border-dashed border-[#334155] bg-[#0B1221] hover:border-[#4DA6FF]/50 p-3 transition-colors flex items-center gap-2"
                  >
                    <G className={`w-4 h-4 shrink-0 ${isStay ? "text-[#94A3B8]" : g.cls}`} />
                    <div className="min-w-0">
                      <div className="text-sm text-[#F8FAFC] truncate">{t(`journal.logType.${l.log_type}`)}{l.skipper_name ? ` · ${l.skipper_name}` : ""}</div>
                      <div className="font-mono text-[10px] text-[#94A3B8] mt-0.5 truncate">{assetName(l.asset_id)}{l.place_name ? ` · ${l.place_name}` : ""}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReservationDetailDialog({ reservation, onClose, onChanged, assets, owners, users = [] }) {
  const { user } = useAuth();
  const t = useT();
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [linkedLogs, setLinkedLogs] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [deletingLogId, setDeletingLogId] = useState(null);
  const asset = assets.find(a => a.id === reservation.asset_id);
  const ownerObj = owners?.find(o => o.id === reservation.owner_id);
  const requesterName = users.find(u => u.id === reservation.requested_by_user_id)?.name || reservation.requested_by_user_id?.slice(0, 8) || "—";
  const isDirector = user?.role === "owner_director" || user?.role === "super_admin";
  const canVote = isDirector && reservation.status === "pending_approval" &&
    !(reservation.approvals || []).some(a => a.director_user_id === user?.id);
  const canEdit = ["super_admin", "owner_director", "operations_admin"].includes(user?.role) ||
    user?.id === reservation.requested_by_user_id;
  const canDelete = canEdit;

  const reloadLogs = async () => {
    try {
      const res = await api.get(`/daily-logs`, { params: { reservation_id: reservation.id } });
      setLinkedLogs(res.data || []);
    } catch { /* silent */ }
  };
  const reloadAudit = async () => {
    try {
      const res = await api.get(`/reservations/${reservation.id}/audit`);
      setAuditEvents(res.data?.events || []);
    } catch { /* silent — non-directors may not have access */ }
  };

  useEffect(() => {
    reloadLogs();
    reloadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation.id]);

  const vote = async (decision) => {
    try {
      await api.post(`/reservations/${reservation.id}/approve`, { decision, note });
      toast.success(`Reservation ${decision}`);
      onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Vote failed"); }
  };

  const cancel = async () => {
    try {
      await api.patch(`/reservations/${reservation.id}`, { status: "cancelled" });
      toast.success("Reservation cancelled"); onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const markNotUsed = async () => {
    try {
      await api.post(`/reservations/${reservation.id}/not-used`, { not_used: true, reason: voidReason.trim() || null });
      toast.success(t("calendar.detail.markedNotUsed"));
      setVoiding(false); setVoidReason(""); onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const markUsed = async () => {
    try {
      await api.post(`/reservations/${reservation.id}/not-used`, { not_used: false });
      toast.success(t("calendar.detail.markedUsed")); onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/reservations/${reservation.id}`);
      toast.success("Reservation deleted"); onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const deleteLog = async (logId) => {
    try {
      await api.delete(`/daily-logs/${logId}`);
      toast.success("Log deleted"); setDeletingLogId(null);
      reloadLogs(); reloadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (editing) {
    return (
      <ReservationDialog
        open
        onOpenChange={(v) => !v && setEditing(false)}
        assets={assets}
        defaultDate={reservation.start_date}
        editing={reservation}
        onSaved={() => { setEditing(false); toast.success("Reservation updated"); onChanged(); onClose(); }}
      />
    );
  }

  if (showLogDialog || editingLog) {
    return (
      <JournalDialog
        open
        onOpenChange={(v) => !v && (setShowLogDialog(false), setEditingLog(null))}
        assets={assets}
        reservations={[reservation]}
        editing={editingLog}
        defaultReservationId={reservation.id}
        defaultAssetId={reservation.asset_id}
        onSaved={() => {
          setShowLogDialog(false); setEditingLog(null);
          toast.success("Saved"); reloadLogs(); reloadAudit();
        }}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="reservation-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            {t(`reservation.${reservation.reservation_type}`)}
            <span className={`badge-pill ${STATUS_BADGE[reservation.status] || "badge-neutral"}`}>{t(`status.${reservation.status}`)}</span>
            {reservation.not_used && <span className="badge-pill bg-[#475569]/60 text-[#E2E8F0] border-[#64748B]/40" data-testid="detail-void-badge">{t("calendar.void")}</span>}
            {reservation.is_protected_private_term && <span className="badge-pill badge-protected">{t("status.protected")}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <Row label={t("calendar.detail.asset")} value={asset?.name || "—"} />
          {ownerObj && <Row label={t("common.owner")} value={ownerObj.name} />}
          <Row label={t("calendar.detail.dates")} value={`${fmtDate(reservation.start_date)} → ${fmtDate(reservation.end_date)}`} />
          {reservation.client_name && <Row label={t("calendar.detail.client")} value={reservation.client_name} />}
          {reservation.passenger_count != null && <Row label={t("calendar.detail.passengers")} value={reservation.passenger_count} />}
          {reservation.trip_price != null && <Row label={t("calendar.detail.price")} value={`€ ${reservation.trip_price}`} />}
          {reservation.notes && <Row label={t("common.notes")} value={reservation.notes} />}
          <Row label={t("calendar.detail.requestedBy")} value={requesterName} />

          {reservation.not_used && (
            <div className="bg-[#475569]/15 border border-[#64748B]/40 rounded-md p-3" data-testid="detail-notused-info">
              <div className="flex items-center gap-2 mb-1">
                <CloudRain className="w-3.5 h-3.5 text-[#94A3B8]" />
                <span className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("calendar.void")}</span>
              </div>
              <div className="text-sm text-[#F8FAFC]">{reservation.not_used_reason || t("calendar.detail.noReason")}</div>
              {reservation.not_used_by_name && (
                <div className="font-mono text-[10px] text-[#94A3B8] mt-1">
                  {reservation.not_used_by_name}{reservation.not_used_at ? ` · ${fmtDate(reservation.not_used_at.slice(0, 10))}` : ""}
                </div>
              )}
            </div>
          )}

          {(reservation.approvals || []).length > 0 && (
            <div>
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] mt-4 mb-1">{t("calendar.detail.approvals")}</div>
              <div className="space-y-1">
                {reservation.approvals.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2">
                    <span>{a.director_name}</span>
                    <span className={`badge-pill ${a.decision === "approved" ? "badge-approved" : "badge-rejected"}`}>{t(`status.${a.decision}`)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canVote && (
            <div className="pt-3 border-t border-[#1E293B] space-y-2">
              <div className="font-mono text-[10px] tracking-widest text-[#F59E0B]">{t("calendar.detail.yourVote")}</div>
              <Textarea
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder={t("calendar.detail.notePlaceholder")}
                data-testid="approval-note"
                className="bg-[#050A14] border-[#1E293B] text-[#F8FAFC]"
              />
              <div className="flex gap-2">
                <Button onClick={() => vote("approved")} data-testid="approve-btn" className="bg-[#10B981] hover:bg-[#059669] flex-1"><Check className="w-4 h-4 mr-1" /> {t("calendar.detail.approve")}</Button>
                <Button onClick={() => vote("rejected")} data-testid="reject-btn" className="bg-[#EF4444] hover:bg-[#DC2626] flex-1"><X className="w-4 h-4 mr-1" /> {t("calendar.detail.reject")}</Button>
              </div>
            </div>
          )}

          {reservation.status !== "cancelled" && (canEdit || canDelete) && (
            <div className="pt-3 border-t border-[#1E293B] space-y-2">
              <div className="flex gap-2 flex-wrap">
                {canEdit && (
                  <Button
                    onClick={() => setEditing(true)} data-testid="edit-reservation-btn"
                    className="flex-1 bg-[#0B1221] border border-[#1E293B] hover:border-[#4DA6FF]/50 hover:bg-[#131C31] text-[#F8FAFC]"
                  >
                    <Pencil className="w-4 h-4 mr-1" /> {t("common.edit")}
                  </Button>
                )}
                {canEdit && (
                  <Button
                    onClick={cancel} data-testid="cancel-reservation-btn"
                    className="flex-1 bg-[#0B1221] border border-[#1E293B] hover:border-[#F59E0B]/50 hover:bg-[#131C31] text-[#FBBF24]"
                  >
                    <Info className="w-4 h-4 mr-1" /> {t("common.cancel")}
                  </Button>
                )}
                {canEdit && reservation.status === "approved" && !reservation.not_used && (
                  <Button
                    onClick={() => setVoiding(true)} data-testid="mark-notused-btn"
                    className="flex-1 bg-[#0B1221] border border-[#1E293B] hover:border-[#64748B] hover:bg-[#131C31] text-[#CBD5E1]"
                  >
                    <CloudRain className="w-4 h-4 mr-1" /> {t("calendar.detail.markNotUsed")}
                  </Button>
                )}
                {canEdit && reservation.not_used && (
                  <Button
                    onClick={markUsed} data-testid="mark-used-btn"
                    className="flex-1 bg-[#0B1221] border border-[#1E293B] hover:border-[#10B981]/50 hover:bg-[#131C31] text-[#34D399]"
                  >
                    <Check className="w-4 h-4 mr-1" /> {t("calendar.detail.markUsed")}
                  </Button>
                )}
                {canDelete && !confirmDelete && (
                  <Button
                    onClick={() => setConfirmDelete(true)} data-testid="delete-reservation-btn"
                    className="flex-1 bg-[#0B1221] border border-[#1E293B] hover:border-[#EF4444]/50 hover:bg-[#131C31] text-[#F87171]"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> {t("common.delete")}
                  </Button>
                )}
              </div>
              {confirmDelete && (
                <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-md p-3" data-testid="confirm-delete-panel">
                  <div className="text-xs text-[#F87171] mb-2">
                    {t("calendar.detail.confirmDelete")}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={doDelete} data-testid="confirm-delete-btn" className="flex-1 bg-[#EF4444] hover:bg-[#DC2626]">{t("calendar.detail.yesDelete")}</Button>
                    <Button onClick={() => setConfirmDelete(false)} data-testid="cancel-delete-btn" className="flex-1 bg-[#050A14] border border-[#1E293B] hover:bg-[#131C31]">{t("common.keep")}</Button>
                  </div>
                </div>
              )}
              {voiding && (
                <div className="bg-[#475569]/15 border border-[#64748B]/40 rounded-md p-3" data-testid="notused-panel">
                  <div className="text-xs text-[#CBD5E1] mb-2">{t("calendar.detail.notUsedPrompt")}</div>
                  <Textarea
                    value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                    placeholder={t("calendar.detail.notUsedReasonPlaceholder")}
                    data-testid="notused-reason-input"
                    className="bg-[#050A14] border-[#1E293B] text-[#F8FAFC] mb-2"
                  />
                  <div className="flex gap-2">
                    <Button onClick={markNotUsed} data-testid="confirm-notused-btn" className="flex-1 bg-[#475569] hover:bg-[#64748B] text-white">{t("calendar.detail.confirmNotUsed")}</Button>
                    <Button onClick={() => { setVoiding(false); setVoidReason(""); }} data-testid="cancel-notused-btn" className="flex-1 bg-[#050A14] border border-[#1E293B] hover:bg-[#131C31]">{t("common.cancel")}</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Attached daily logs */}
          <div className="pt-3 border-t border-[#1E293B] space-y-2" data-testid="res-linked-logs">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] flex items-center gap-1">
                <BookOpen className="w-3 h-3" /> {t("calendar.detail.linkedLogs")}
              </div>
              <Button size="sm" onClick={() => setShowLogDialog(true)} data-testid="res-add-log-btn"
                className="h-7 text-xs bg-[#0B1221] border border-[#1E293B] hover:border-[#4DA6FF]/50 hover:bg-[#131C31]">
                <Plus className="w-3 h-3 mr-1" /> {t("calendar.detail.addLog")}
              </Button>
            </div>
            {linkedLogs.length === 0 ? (
              <div className="text-xs text-[#94A3B8]">{t("calendar.detail.noLinkedLogs")}</div>
            ) : (
              <div className="space-y-2">
                {linkedLogs.map(l => (
                  <div key={l.id} className="bg-[#050A14] border border-[#1E293B] rounded-md p-3" data-testid={`linked-log-${l.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`badge-pill ${l.log_type === "fishing_log" ? "badge-fishing" : "badge-neutral"}`}>
                            {l.log_type === "fishing_log" && <Fish className="w-3 h-3" />}
                            {t(`journal.logType.${l.log_type}`)}
                          </span>
                          <span className="font-mono text-[10px] text-[#94A3B8]">{fmtDate(l.log_date)}</span>
                        </div>
                        <div className="text-xs font-medium truncate">{l.place_name || l.route || t("journal.untitled")}</div>
                        {l.incidents && <div className="text-[11px] text-[#94A3B8] line-clamp-1 mt-0.5">{l.incidents}</div>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditingLog(l)} data-testid={`edit-log-${l.id}`}
                          className="p-1.5 rounded-md bg-[#0B1221] border border-[#1E293B] hover:border-[#4DA6FF] text-[#94A3B8]" title={t("common.edit")}>
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button onClick={() => setDeletingLogId(l.id)} data-testid={`delete-log-${l.id}`}
                          className="p-1.5 rounded-md bg-[#0B1221] border border-[#1E293B] hover:border-[#EF4444] text-[#F87171]" title={t("common.delete")}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {deletingLogId === l.id && (
                      <div className="mt-2 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-md p-2" data-testid={`confirm-delete-log-${l.id}`}>
                        <div className="text-[11px] text-[#F87171] mb-2">{t("calendar.detail.confirmDeleteLog")}</div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => deleteLog(l.id)} data-testid={`confirm-delete-log-btn-${l.id}`} className="h-7 flex-1 bg-[#EF4444] hover:bg-[#DC2626] text-xs">{t("calendar.detail.yesDelete")}</Button>
                          <Button size="sm" onClick={() => setDeletingLogId(null)} className="h-7 flex-1 bg-[#050A14] border border-[#1E293B] hover:bg-[#131C31] text-xs">{t("common.keep")}</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Edit history (audit trail) */}
          {auditEvents.length > 0 && (
            <div className="pt-3 border-t border-[#1E293B] space-y-2" data-testid="res-audit-strip">
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] flex items-center gap-1">
                <History className="w-3 h-3" /> {t("calendar.detail.history")}
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
                {auditEvents.map(e => {
                  const action = e.action_type?.replace("vote_", "vote_");
                  const actionLabel = t(`audit.action.${action}`) || action;
                  const ts = new Date(e.created_at).toLocaleString("en-GB", {day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"});
                  const fields = (e.changed_fields || []).filter(f => !["updated_at", "approvals", "created_at", "id"].includes(f));
                  return (
                    <div key={e.id} className="text-[11px] bg-[#050A14] border border-[#1E293B] rounded-md px-2 py-1.5" data-testid={`audit-event-${e.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[#94A3B8] whitespace-nowrap">{ts}</span>
                        <span className="font-medium text-[#4DA6FF]">{e.actor_name}</span>
                        <span className="text-[#94A3B8]">{actionLabel}</span>
                      </div>
                      {fields.length > 0 && (
                        <div className="font-mono text-[10px] text-[#94A3B8] mt-0.5 break-words">
                          {t("audit.changed")}: {fields.slice(0, 8).join(", ")}{fields.length > 8 ? "…" : ""}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[#1E293B] pb-2">
      <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] whitespace-nowrap">{label.toUpperCase()}</span>
      <span className={`text-right break-words ${mono ? "font-mono text-xs text-[#94A3B8]" : "text-sm"}`}>{value}</span>
    </div>
  );
}
